/**
 * DealInlineEditor — Section 1: Inline Editing Table + Section 2: CSV Import Panel
 * Used inside DataHealthDashboard as a standalone admin tool.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import {
  ChevronUp, ChevronDown, Save, X, RotateCcw, Upload,
  Search, Filter, RefreshCw, FileText, CheckCircle, AlertCircle,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

// Columns definition for inline editing
const COLUMNS = [
  { key: 'deal_name',      label: 'Deal Name',      type: 'text',   required: true, width: 'min-w-[160px]' },
  { key: 'merchant_name',  label: 'Merchant Name',  type: 'text',   required: false, width: 'min-w-[140px]' },
  { key: 'status',         label: 'Status',         type: 'select', required: false, width: 'min-w-[120px]',
    options: ['Active','Funded','Current','Payment Issues','In-House Collection','Default','Defaulted','Refinanced','Collected','Paid in Full','Cancelled'] },
  { key: 'funding_date',   label: 'Funding Date',   type: 'date',   required: false, width: 'min-w-[130px]' },
  { key: 'funding_amount', label: 'Advance Amt',    type: 'number', required: true,  width: 'min-w-[110px]' },
  { key: 'rate',           label: 'Factor Rate',    type: 'number', required: true,  width: 'min-w-[100px]', step: '0.00001' },
  { key: 'term_days',      label: 'Term (Days)',    type: 'number', required: false, width: 'min-w-[90px]' },
  { key: 'fees',           label: 'Fee Amount',     type: 'number', required: false, width: 'min-w-[100px]' },
];

const STATUS_OPTIONS = ['All', 'Active', 'Funded', 'Current', 'Payment Issues', 'In-House Collection', 'Default', 'Defaulted', 'Refinanced', 'Collected', 'Paid in Full', 'Cancelled'];

const fmt = (n) => n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); }
  catch { return d?.slice?.(0, 10) || '—'; }
};

// ── CSV detection helpers ─────────────────────────────────────────────────
const DEALS_REQUIRED_HEADERS = ['Deal Name', 'Advance Amount', 'Factor Rate'];
const TX_REQUIRED_HEADERS     = ['Transaction ID', 'Amount', 'Transaction Type'];

function detectCsvType(headers) {
  if (DEALS_REQUIRED_HEADERS.every(h => headers.includes(h))) return 'deals';
  if (TX_REQUIRED_HEADERS.every(h => headers.includes(h))) return 'transactions';
  return null;
}

function parseCSV(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;
  const headers = [];
  let cur = ''; let inQ = false;
  for (const ch of lines[0]) {
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { headers.push(cur.trim()); cur = ''; }
    else { cur += ch; }
  }
  headers.push(cur.trim());

  const rows = lines.slice(1).map(line => {
    const vals = []; let c = ''; let q = false;
    for (const ch of line) {
      if (ch === '"') { q = !q; }
      else if (ch === ',' && !q) { vals.push(c); c = ''; }
      else { c += ch; }
    }
    vals.push(c);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
    return obj;
  });
  return { headers, rows };
}

// ── Pagination bar ────────────────────────────────────────────────────────
const PaginationBar = ({ total, page, pageSize, onPage }) => {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between px-4 py-2 border-t text-sm text-gray-600">
      <span>Showing {start}–{end} of {total} deals</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(1)} disabled={page === 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronsLeft className="h-4 w-4" /></button>
        <button onClick={() => onPage(page - 1)} disabled={page === 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
        <span className="px-2">Page {page} / {totalPages}</span>
        <button onClick={() => onPage(page + 1)} disabled={page === totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => onPage(totalPages)} disabled={page === totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronsRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────
const DealInlineEditor = ({ token, tenant }) => {
  // Section 1: table state
  const [deals, setDeals]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('All');
  const [sort, setSort]             = useState({ by: 'deal_id', dir: 'asc' });
  const [dirtyRows, setDirtyRows]   = useState({}); // {dealUuid: {field: value}}
  const [savingRows, setSavingRows] = useState(new Set());

  // Section 2: CSV import state machine
  const [csvStep, setCsvStep]       = useState(null); // null | 'preview' | 'importing' | 'results'
  const [csvFile, setCsvFile]       = useState(null);
  const [csvParsed, setCsvParsed]   = useState(null); // {headers, rows, type}
  const [csvPreviewData, setCsvPreviewData] = useState(null); // {existing, new, errors}
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResults, setCsvResults] = useState(null);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'X-Tenant': tenant,
    'Content-Type': 'application/json',
  }), [token, tenant]);

  // ── Fetch deals ──────────────────────────────────────────────────────
  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/deals?limit=2000`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
      });
      if (!res.ok) throw new Error('Failed to fetch deals');
      const data = await res.json();
      setDeals(Array.isArray(data) ? data : data.deals || []);
      setPage(1);
    } catch (err) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [token, tenant]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // ── Filter + sort + paginate ─────────────────────────────────────────
  const filtered = (() => {
    let rows = deals;
    if (statusFilter !== 'All') rows = rows.filter(d => d.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(d =>
        (d.deal_name || '').toLowerCase().includes(q) ||
        (d.merchant_name || '').toLowerCase().includes(q)
      );
    }
    rows = [...rows].sort((a, b) => {
      let av = a[sort.by] ?? '', bv = b[sort.by] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      return sort.dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return rows;
  })();

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    setSort(prev => ({
      by: field,
      dir: prev.by === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  // ── Inline cell change ───────────────────────────────────────────────
  const handleCellChange = (dealId, field, value) => {
    setDirtyRows(prev => ({
      ...prev,
      [dealId]: { ...(prev[dealId] || {}), [field]: value },
    }));
  };

  const getDisplayValue = (deal, field) => {
    const dirty = dirtyRows[deal.id];
    return dirty && field in dirty ? dirty[field] : deal[field];
  };

  const isCellDirty = (deal, field) =>
    dirtyRows[deal.id] && field in dirtyRows[deal.id];

  const isRowDirty = (deal) =>
    dirtyRows[deal.id] && Object.keys(dirtyRows[deal.id]).length > 0;

  const handleDiscard = (dealId) => {
    setDirtyRows(prev => { const n = { ...prev }; delete n[dealId]; return n; });
  };

  // ── Save row ─────────────────────────────────────────────────────────
  const handleSave = async (deal) => {
    const changes = dirtyRows[deal.id];
    if (!changes) return;

    setSavingRows(prev => new Set([...prev, deal.id]));
    try {
      // Convert funding_date to ISO if needed
      const patch = { ...changes };
      if (patch.funding_date && !patch.funding_date.includes('T')) {
        patch.funding_date = new Date(patch.funding_date).toISOString();
      }
      // Convert numeric strings
      const financialFields = ['funding_amount', 'rate', 'term_days', 'fees'];
      for (const numField of financialFields) {
        if (patch[numField] !== undefined) {
          const n = parseFloat(patch[numField]);
          patch[numField] = isNaN(n) ? undefined : n;
        }
      }
      const needsRecalc = financialFields.some(f => f in changes);

      const res = await fetch(`${API_URL}/api/deals/${deal.id}`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }

      // Only recalculate when financial fields changed (payback = advance × rate, then rebuild balance)
      if (needsRecalc) {
        await fetch(`${API_URL}/api/deals/${deal.id}/recalculate`, {
          method: 'POST',
          headers: headers(),
        });
      }

      handleDiscard(deal.id);
      toast.success(`Deal "${deal.deal_name || deal.deal_id}" saved`);
      fetchDeals(); // refresh row with recalculated values
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSavingRows(prev => { const n = new Set(prev); n.delete(deal.id); return n; });
    }
  };

  // ── Section 2 CSV: Dropzone ──────────────────────────────────────────
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setCsvFile(file);

    const text = await file.text();
    const parsed = parseCSV(text);
    if (!parsed) { toast.error('Could not parse CSV — file appears empty'); return; }

    const type = detectCsvType(parsed.headers);
    if (!type) {
      toast.error('Unrecognized CSV format. Expected Deals or Transactions export headers.');
      return;
    }

    // Extract IDs for preview check
    const idField = type === 'deals' ? 'Deal ID' : 'Transaction ID';
    const ids = parsed.rows.map(r => r[idField]).filter(Boolean);

    const checkEndpoint = type === 'deals'
      ? '/api/deals/csv-check-ids'
      : '/api/transactions/csv-check-ids';
    const bodyKey = type === 'deals' ? 'deal_ids' : 'transaction_ids';

    try {
      const res = await fetch(`${API_URL}${checkEndpoint}`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ [bodyKey]: ids }),
      });
      const check = await res.json();
      const existing = check.existing_ids || [];
      const newIds   = ids.filter(id => !existing.includes(id));

      // Validate required fields per row
      const reqHeaders = type === 'deals'
        ? ['Deal Name', 'Advance Amount', 'Factor Rate']
        : ['Deal ID', 'Amount', 'Transaction Type'];
      const rowErrors = parsed.rows.reduce((acc, row, i) => {
        const missing = reqHeaders.filter(h => !row[h] || !String(row[h]).trim());
        if (missing.length) acc.push({ row: i + 1, error: `Missing: ${missing.join(', ')}` });
        return acc;
      }, []);

      setCsvParsed({ ...parsed, type });
      setCsvPreviewData({ existing: existing.length, new: newIds.length, errors: rowErrors });
      setCsvStep('preview');
    } catch (err) {
      toast.error('Preview check failed — ' + (err.message || 'unknown error'));
    }
  }, [headers]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
  });

  // ── Section 2 CSV: Import ────────────────────────────────────────────
  const handleCsvImport = async () => {
    if (!csvParsed) return;
    setCsvImporting(true);
    setCsvStep('importing');

    const endpoint = csvParsed.type === 'deals'
      ? '/api/deals/csv-import'
      : '/api/transactions/csv-import';

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ rows: csvParsed.rows }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Import failed');

      setCsvResults({
        created: result.created || 0,
        updated: result.updated || 0,
        skipped: result.skipped || 0,
        errors:  result.errors  || [],
      });
      setCsvStep('results');

      // Refresh the deals table if we imported deals
      if (csvParsed.type === 'deals') fetchDeals();
    } catch (err) {
      toast.error(err.message || 'Import failed');
      setCsvStep('preview');
    } finally {
      setCsvImporting(false);
    }
  };

  const resetCsv = () => {
    setCsvStep(null); setCsvFile(null); setCsvParsed(null);
    setCsvPreviewData(null); setCsvResults(null); setCsvImporting(false);
  };

  // ── Render: sort icon ────────────────────────────────────────────────
  const SortIcon = ({ field }) => {
    if (sort.by !== field) return <ChevronUp className="h-3 w-3 text-gray-300" />;
    return sort.dir === 'asc'
      ? <ChevronUp className="h-3 w-3 text-blue-500" />
      : <ChevronDown className="h-3 w-3 text-blue-500" />;
  };

  // ── Render: editable cell ────────────────────────────────────────────
  const EditableCell = ({ deal, col }) => {
    const value = getDisplayValue(deal, col.key);
    const dirty = isCellDirty(deal, col.key);
    const cellClass = `px-2 py-1.5 ${dirty ? 'bg-amber-50 ring-1 ring-amber-400 rounded' : ''}`;

    if (col.type === 'select') {
      return (
        <td className={`px-3 py-2 ${col.width}`}>
          <div className={cellClass}>
            <select
              className="w-full text-xs bg-transparent border-0 outline-none cursor-pointer"
              value={value || ''}
              onChange={e => handleCellChange(deal.id, col.key, e.target.value)}
              data-testid={`cell-${col.key}-${deal.id}`}
            >
              {col.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </td>
      );
    }

    if (col.type === 'date') {
      const dateVal = value ? String(value).slice(0, 10) : '';
      return (
        <td className={`px-3 py-2 ${col.width}`}>
          <div className={cellClass}>
            <input
              type="date"
              className="w-full text-xs bg-transparent border-0 outline-none"
              value={dirty ? (value || '') : dateVal}
              onChange={e => handleCellChange(deal.id, col.key, e.target.value)}
              data-testid={`cell-${col.key}-${deal.id}`}
            />
          </div>
        </td>
      );
    }

    return (
      <td className={`px-3 py-2 ${col.width}`}>
        <div className={cellClass}>
          <input
            type={col.type === 'number' ? 'number' : 'text'}
            step={col.step}
            className="w-full text-xs bg-transparent border-0 outline-none"
            value={value ?? ''}
            onChange={e => handleCellChange(deal.id, col.key, e.target.value)}
            onFocus={e => e.target.select()}
            data-testid={`cell-${col.key}-${deal.id}`}
          />
        </div>
      </td>
    );
  };

  // ── RENDER ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="deal-inline-editor">

      {/* ── Section 1: Inline Editing Table ───────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-blue-600" />
              Inline Deal Editor
              {deals.length > 0 && (
                <Badge variant="outline" className="ml-1">{filtered.length} deals</Badge>
              )}
            </CardTitle>
            <button
              onClick={fetchDeals}
              disabled={loading}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-40"
              data-testid="refresh-deals-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search deal or merchant name…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-7 pr-3 py-1.5 w-full border rounded text-sm outline-none focus:ring-1 focus:ring-blue-400"
                data-testid="inline-search-input"
              />
            </div>
            <div className="flex items-center gap-1">
              <Filter className="h-3.5 w-3.5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={e => { setStatus(e.target.value); setPage(1); }}
                className="border rounded px-2 py-1.5 text-sm bg-white outline-none"
                data-testid="status-filter-select"
              >
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="inline-edit-table">
                  <thead>
                    <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        <span className="flex items-center gap-1 cursor-pointer select-none" onClick={() => handleSort('deal_id')}>
                          Deal ID <SortIcon field="deal_id" />
                        </span>
                      </th>
                      {COLUMNS.map(col => (
                        <th
                          key={col.key}
                          className={`px-3 py-2 text-left font-medium whitespace-nowrap ${col.width}`}
                        >
                          <span className="flex items-center gap-1 cursor-pointer select-none" onClick={() => handleSort(col.key)}>
                            {col.label} <SortIcon field={col.key} />
                          </span>
                        </th>
                      ))}
                      <th className="px-3 py-2 text-center font-medium whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginated.length === 0 ? (
                      <tr><td colSpan={COLUMNS.length + 2} className="text-center py-12 text-gray-400">No deals found</td></tr>
                    ) : paginated.map(deal => {
                      const dirty = isRowDirty(deal);
                      const saving = savingRows.has(deal.id);
                      return (
                        <tr
                          key={deal.id}
                          className={`hover:bg-gray-50 ${dirty ? 'bg-amber-50/30' : ''}`}
                          data-testid={`inline-row-${deal.id}`}
                        >
                          <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">
                            {deal.deal_id}
                          </td>
                          {COLUMNS.map(col => (
                            <EditableCell key={col.key} deal={deal} col={col} />
                          ))}
                          <td className="px-3 py-2">
                            {dirty ? (
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                <button
                                  onClick={() => handleSave(deal)}
                                  disabled={saving}
                                  className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                  data-testid={`save-row-btn-${deal.id}`}
                                >
                                  <Save className="h-3 w-3" />
                                  {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  onClick={() => handleDiscard(deal.id)}
                                  disabled={saving}
                                  className="flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 disabled:opacity-50"
                                  data-testid={`discard-row-btn-${deal.id}`}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                  Discard
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                total={filtered.length}
                page={page}
                pageSize={PAGE_SIZE}
                onPage={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Section 2: CSV Import Panel ───────────────────────────── */}
      <Card data-testid="csv-import-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-5 w-5 text-blue-600" />
            CSV Import
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Upload a Deals or Transactions CSV export. Existing records are updated; new records are created.
          </p>
        </CardHeader>

        <CardContent>
          {/* Step 1: Drop zone */}
          {!csvStep && (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors
                ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
              data-testid="csv-dropzone"
            >
              <input {...getInputProps()} data-testid="csv-file-input-section2" />
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">
                {isDragActive ? 'Drop the CSV file here' : 'Drag & drop a CSV file, or click to browse'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Accepts Deals or Transactions exports (.csv)</p>
            </div>
          )}

          {/* Step 2: Preview */}
          {csvStep === 'preview' && csvParsed && csvPreviewData && (
            <div className="space-y-4" data-testid="csv-preview-section">
              {/* Summary bar */}
              <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="font-medium text-blue-800">
                  {csvParsed.type === 'deals' ? 'Deals' : 'Transactions'} CSV — {csvParsed.rows.length} records
                </span>
                <span className="text-green-700 font-medium">{csvPreviewData.new} new</span>
                <span className="text-gray-500">·</span>
                <span className="text-amber-700 font-medium">{csvPreviewData.existing} existing (will update)</span>
                {csvPreviewData.errors.length > 0 && (
                  <span className="ml-auto text-red-600 font-medium flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {csvPreviewData.errors.length} rows with errors
                  </span>
                )}
              </div>

              {/* Error list */}
              {csvPreviewData.errors.length > 0 && (
                <div className="max-h-32 overflow-y-auto border border-red-200 rounded p-2 bg-red-50 text-xs space-y-0.5">
                  {csvPreviewData.errors.map((e, i) => (
                    <div key={i} className="text-red-700">Row {e.row}: {e.error}</div>
                  ))}
                </div>
              )}

              {/* Preview table */}
              <div className="overflow-x-auto rounded border">
                <table className="w-full text-xs" data-testid="csv-preview-table">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600">
                      {csvParsed.headers.slice(0, 8).map(h => (
                        <th key={h} className="px-2 py-1.5 text-left border-b font-medium whitespace-nowrap">{h}</th>
                      ))}
                      {csvParsed.headers.length > 8 && (
                        <th className="px-2 py-1.5 text-left border-b text-gray-400">+{csvParsed.headers.length - 8} more</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {csvParsed.rows.slice(0, 10).map((row, i) => {
                      const hasError = csvPreviewData.errors.some(e => e.row === i + 1);
                      return (
                        <tr key={i} className={hasError ? 'bg-red-50' : 'hover:bg-gray-50'}>
                          {csvParsed.headers.slice(0, 8).map(h => (
                            <td key={h} className="px-2 py-1 border-b text-gray-700 max-w-[140px] truncate">
                              {row[h] || <span className="text-gray-300">—</span>}
                            </td>
                          ))}
                          {csvParsed.headers.length > 8 && <td className="px-2 py-1 border-b text-gray-300">…</td>}
                        </tr>
                      );
                    })}
                    {csvParsed.rows.length > 10 && (
                      <tr><td colSpan={9} className="px-2 py-1 text-center text-gray-400 text-xs">
                        … and {csvParsed.rows.length - 10} more rows
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={resetCsv} className="px-4 py-2 border rounded text-sm hover:bg-gray-100">
                  Cancel
                </button>
                <button
                  onClick={handleCsvImport}
                  disabled={csvPreviewData.errors.length === csvParsed.rows.length}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2"
                  data-testid="csv-confirm-import-btn"
                >
                  <Upload className="h-4 w-4" />
                  Confirm Import ({csvParsed.rows.length} records)
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Importing */}
          {csvStep === 'importing' && (
            <div className="flex flex-col items-center gap-4 py-12" data-testid="csv-importing-state">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <p className="text-sm text-gray-600">Importing {csvParsed?.rows?.length} records…</p>
              <p className="text-xs text-gray-400">Running upsert and recalculating affected deals</p>
            </div>
          )}

          {/* Step 4: Results */}
          {csvStep === 'results' && csvResults && (
            <div className="space-y-4" data-testid="csv-results-section">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Import complete</p>
                  <p className="text-sm text-green-700">
                    {csvResults.created} created · {csvResults.updated} updated · {csvResults.skipped} skipped
                  </p>
                </div>
              </div>

              {csvResults.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Skipped rows</p>
                  <div className="max-h-40 overflow-y-auto border rounded text-xs divide-y">
                    {csvResults.errors.map((e, i) => (
                      <div key={i} className="px-3 py-1.5 text-red-700">
                        Row {e.row}: {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={resetCsv}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  data-testid="csv-done-btn"
                >
                  Done — Import Another
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DealInlineEditor;
